import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Printer, Download, Calendar, User, Building, FileText } from 'lucide-react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PrintSalarySlip = () => {
  const [searchParams] = useSearchParams();
  const urlStaffId = searchParams.get('staff_id');
  const urlMonth = searchParams.get('month');
  
  const [filters, setFilters] = useState({ 
    month: urlMonth || new Date().toISOString().slice(0, 7), 
    staff_id: urlStaffId || '' 
  });
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [salarySlips, setSalarySlips] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Institution',
    address: '',
    phone: '',
    email: ''
  });
  
  const printRef = useRef();

  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = -12; i <= 0; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options.reverse();
  };

  const monthOptions = getMonthOptions();

  useEffect(() => {
    fetchStaffList();
    fetchCompanyInfo();
  }, []);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStaffList(response.data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/settings/college`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        setCompanyInfo({
          name: response.data.name || 'Institution',
          address: response.data.address || '',
          phone: response.data.phone || '',
          email: response.data.email || ''
        });
      }
    } catch (err) {
      console.error('Error fetching company info:', err);
    }
  };

  const loadSalarySlips = async () => {
    setLoading(true);
    setSalarySlips([]);
    setSelectedSlip(null);

    try {
      const token = localStorage.getItem('token');
      
      // Get payroll data for the month
      const response = await axios.get(`${API_URL}/api/salary/payroll/${filters.month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data && response.data.records) {
        let slips = response.data.records;
        
        // Filter by staff if selected
        if (filters.staff_id) {
          slips = slips.filter(s => s.staff_id === filters.staff_id);
        }
        
        // Only show paid slips
        slips = slips.filter(s => s.status === 'Paid' || s.net_payable > 0);
        
        setSalarySlips(slips);
        
        // Auto-select if only one slip (from URL params)
        if (slips.length === 1) {
          setSelectedSlip(slips[0]);
        }
        
        if (slips.length === 0) {
          alert('No salary data found for selected month');
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        alert('No payroll generated for this month. Please generate payroll first.');
      } else {
        console.error('Error loading salary slips:', err);
        alert('Failed to load salary slips');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch salary slips when URL params are present
  useEffect(() => {
    if (urlStaffId || urlMonth) {
      loadSalarySlips();
    }
  }, [urlStaffId, urlMonth]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Salary_Slip_${selectedSlip?.full_name}_${filters.month}`,
  });

  // Fallback print function if useReactToPrint doesn't work
  const printSlip = () => {
    if (handlePrint) {
      handlePrint();
    } else {
      // Fallback to window.print
      const printContent = printRef.current;
      if (printContent) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Salary Slip - ${selectedSlip?.full_name}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f5f5f5; }
                .text-center { text-align: center; }
                .text-right { text-align: right; }
                .font-bold { font-weight: bold; }
                .border-b { border-bottom: 1px solid #333; }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMonthLabel = () => {
    return monthOptions.find(m => m.value === filters.month)?.label || filters.month;
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Print Salary Slip</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and print salary slips for staff</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Select Month
            </label>
            <select 
              className="w-full px-3 py-2 border rounded-lg bg-white"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-4 w-4 mr-1" />
              Staff Member
            </label>
            <select 
              className="w-full px-3 py-2 border rounded-lg bg-white"
              value={filters.staff_id}
              onChange={(e) => setFilters({ ...filters, staff_id: e.target.value })}
            >
              <option value="">All Staff</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.full_name} ({staff.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={loadSalarySlips}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load Salary Slips'}
            </button>
          </div>
          <div className="flex items-end">
            {selectedSlip && (
              <button 
                onClick={printSlip}
                className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <Printer className="h-4 w-4" />
                Print Slip
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Staff List */}
      {salarySlips.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">Select Staff to Print Slip ({salarySlips.length} found)</h3>
          </div>
          <div className="divide-y">
            {salarySlips.map(slip => (
              <div 
                key={slip.staff_id}
                onClick={() => setSelectedSlip(slip)}
                className={`p-4 cursor-pointer hover:bg-blue-50 transition-colors flex justify-between items-center
                  ${selectedSlip?.staff_id === slip.staff_id ? 'bg-blue-100 border-l-4 border-blue-600' : ''}`}
              >
                <div>
                  <div className="font-medium">{slip.full_name}</div>
                  <div className="text-sm text-gray-500">{slip.employee_id} | {slip.department}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-green-600">{formatCurrency(slip.net_payable)}</div>
                  <div className={`text-xs px-2 py-1 rounded-full ${slip.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {slip.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Salary Slip Preview */}
      {selectedSlip && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Salary Slip Preview</h3>
            <button 
              onClick={printSlip}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
          </div>
          
          {/* Printable Salary Slip */}
          <div ref={printRef} className="bg-white p-8 border">
            {/* Header */}
            <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
              <h1 className="text-2xl font-bold text-gray-900">{companyInfo.name}</h1>
              {companyInfo.address && <p className="text-gray-600">{companyInfo.address}</p>}
              <p className="text-lg font-semibold mt-2 bg-gray-100 py-2">SALARY SLIP - {getMonthLabel()}</p>
            </div>

            {/* Employee Details */}
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div className="space-y-2">
                <div className="flex">
                  <span className="w-32 font-semibold">Employee ID:</span>
                  <span>{selectedSlip.employee_id}</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-semibold">Name:</span>
                  <span>{selectedSlip.full_name}</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-semibold">Department:</span>
                  <span>{selectedSlip.department}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex">
                  <span className="w-32 font-semibold">Designation:</span>
                  <span>{selectedSlip.designation}</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-semibold">Pay Period:</span>
                  <span>{getMonthLabel()}</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-semibold">Working Days:</span>
                  <span>{selectedSlip.present_days || 0} / {selectedSlip.working_days || 0}</span>
                </div>
              </div>
            </div>

            {/* Earnings & Deductions */}
            <div className="grid grid-cols-2 gap-8 mb-6">
              {/* Earnings */}
              <div>
                <h4 className="font-bold text-center bg-gray-200 py-2 mb-2">EARNINGS</h4>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Basic Salary</td>
                      <td className="text-right">{formatCurrency(selectedSlip.basic_salary)}</td>
                    </tr>
                    {selectedSlip.allowances?.map((allowance, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-1">{allowance.name}</td>
                        <td className="text-right">{formatCurrency(allowance.calculated_value)}</td>
                      </tr>
                    )) || (
                      <tr className="border-b">
                        <td className="py-1">Allowances</td>
                        <td className="text-right">{formatCurrency(selectedSlip.total_allowances)}</td>
                      </tr>
                    )}
                    <tr className="font-bold bg-gray-100">
                      <td className="py-2">Gross Salary</td>
                      <td className="text-right">{formatCurrency(selectedSlip.actual_gross)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Deductions */}
              <div>
                <h4 className="font-bold text-center bg-gray-200 py-2 mb-2">DEDUCTIONS</h4>
                <table className="w-full text-sm">
                  <tbody>
                    {selectedSlip.deductions_list?.map((deduction, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-1">{deduction.name}</td>
                        <td className="text-right">{formatCurrency(deduction.calculated_value)}</td>
                      </tr>
                    )) || (
                      <>
                        <tr className="border-b">
                          <td className="py-1">PF</td>
                          <td className="text-right">{formatCurrency(selectedSlip.actual_deductions * 0.9)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">Professional Tax</td>
                          <td className="text-right">{formatCurrency(selectedSlip.actual_deductions * 0.1)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="font-bold bg-gray-100">
                      <td className="py-2">Total Deductions</td>
                      <td className="text-right">{formatCurrency(selectedSlip.actual_deductions)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Net Salary */}
            <div className="bg-blue-100 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">NET SALARY</span>
                <span className="text-2xl font-bold text-blue-700">{formatCurrency(selectedSlip.net_payable)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                (In Words: {numberToWords(Math.round(selectedSlip.net_payable))} Rupees Only)
              </p>
            </div>

            {/* Payment Info */}
            {selectedSlip.status === 'Paid' && (
              <div className="text-sm text-gray-600 mb-4">
                <p><strong>Payment Status:</strong> Paid</p>
                {selectedSlip.paid_on && <p><strong>Paid On:</strong> {formatDate(selectedSlip.paid_on)}</p>}
                {selectedSlip.payment_method && <p><strong>Payment Method:</strong> {selectedSlip.payment_method}</p>}
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-4 border-t grid grid-cols-2 gap-8 text-sm">
              <div>
                <p className="text-gray-500 mb-8">Employee Signature</p>
                <div className="border-t border-gray-400 w-48"></div>
              </div>
              <div className="text-right">
                <p className="text-gray-500 mb-8">Authorized Signature</p>
                <div className="border-t border-gray-400 w-48 ml-auto"></div>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
              This is a computer-generated salary slip and does not require a signature.
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && salarySlips.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select month and click "Load Salary Slips" to view salary slips</p>
          <p className="text-sm text-gray-400 mt-2">Make sure payroll is generated and salaries are processed for the selected month</p>
        </div>
      )}
    </div>
  );
};

export default PrintSalarySlip;
